/**
 * Reading a turn's deliverables out of a tool result, and identifying them.
 *
 * Neo's charts, maps, forms and dashboard links are *not* provider content —
 * they're app-only blocks the backend appends to an assistant message's
 * ``content_parts``. The catch is *when* it appends them: not when the tool
 * returns, but when it finalises the NEXT model round
 * (``append_chart_blocks`` in ``helper/Neo/turn.py``). A client that waits for
 * the canonical row therefore shows nothing until the turn ends — on a long
 * multi-round turn, a minute or more after the chart was actually ready.
 *
 * It doesn't have to wait. The ``tool_call_result`` frame carries the tool's
 * output verbatim (``_tool_call_to_wire`` sends ``tool_output_json`` as-is),
 * and the block is right there inside it, under the same ``<kind>_artifact`` /
 * ``<kind>_artifacts`` keys the backend's own extractors read. So the client
 * builds the block itself and paints immediately.
 *
 * That leaves the same deliverable arriving twice — once optimistically here,
 * once on the canonical row — which is what {@link chartArtifactKey} and
 * friends are for. See the identity note in ``index.tsx``.
 *
 * Kept out of ``index.tsx`` because it is pure payload-shaped logic that the
 * component can't expose to a test.
 */

/** Block kinds a tool result can carry. Mirrors the ``neo_*`` content-part
 *  types in ``schemas/neo.py``. */
export type NeoArtifactKind =
  | "neo_chart"
  | "neo_map"
  | "neo_form"
  | "neo_dashboard";

/** An artifact lifted from a tool output, still in wire shape. The caller
 *  casts ``part`` to its own content-part type — validation has already
 *  happened, and the field names are the backend's. */
export interface RawNeoArtifact {
  kind: NeoArtifactKind;
  part: Record<string, unknown>;
}

const isNonEmptyString = (value: unknown): boolean =>
  typeof value === "string" && value.trim().length > 0;

const isNonEmptyArray = (value: unknown): boolean =>
  Array.isArray(value) && value.length > 0;

/** Validity per kind, mirroring the backend predicates (``is_neo_chart_block``
 *  and siblings). This is the one path where a block reaches the renderer
 *  without the server having vetted it first, so the check is ours to repeat
 *  rather than assume — a half-built chart is worse than a late one. */
const isRenderable = (
  kind: NeoArtifactKind,
  part: Record<string, unknown>
): boolean => {
  if (part.type !== kind) return false;
  switch (kind) {
    case "neo_chart":
      return isNonEmptyString(part.title) && isNonEmptyArray(part.series);
    case "neo_map":
      // Either render path is enough: a live Mapbox map needs markers, the
      // static fallback needs its PNG.
      return (
        isNonEmptyString(part.title) &&
        (isNonEmptyString(part.image_url) || isNonEmptyArray(part.markers))
      );
    case "neo_form":
      return isNonEmptyString(part.title) && isNonEmptyArray(part.fields);
    case "neo_dashboard":
      return (
        typeof part.dashboard_id === "number" &&
        typeof part.project_id === "number"
      );
  }
};

/** Every renderable artifact a tool's output carries, in authored order.
 *
 *  Both the singular and plural keys are read, because a tool may emit one
 *  chart (``chart_artifact``) or several (``chart_artifacts``) — same as
 *  ``chart_blocks_from_tool_output`` on the backend. */
export const neoArtifactsFromToolOutput = (
  output: unknown
): RawNeoArtifact[] => {
  if (!output || typeof output !== "object" || Array.isArray(output)) return [];
  const raw = output as Record<string, unknown>;
  const found: RawNeoArtifact[] = [];

  const kinds: Array<{ kind: NeoArtifactKind; prefix: string }> = [
    { kind: "neo_chart", prefix: "chart" },
    { kind: "neo_map", prefix: "map" },
    { kind: "neo_form", prefix: "form" },
    { kind: "neo_dashboard", prefix: "dashboard" },
  ];

  for (const { kind, prefix } of kinds) {
    const candidates: unknown[] = [raw[`${prefix}_artifact`]];
    const many = raw[`${prefix}_artifacts`];
    if (Array.isArray(many)) candidates.push(...many);
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== "object") continue;
      const part = candidate as Record<string, unknown>;
      if (isRenderable(kind, part)) found.push({ kind, part });
    }
  }

  return found;
};

/* ── Identity ─────────────────────────────────────────────────────────────
 *
 * Content keys, not positions, so the same deliverable collapses to one card
 * however many times it reaches us. Deliberately cheap — a chart is keyed by
 * its series' ids and lengths, never their samples — because these recompute
 * on every streaming delta over payloads that can hold hundreds of points.
 *
 * Parameters are typed ``object`` so callers can pass their own content-part
 * types without a cast; the wire field names are read off the value here.
 */

export const chartArtifactKey = (chart: object): string => {
  const raw = chart as Record<string, unknown>;
  const series = Array.isArray(raw.series) ? raw.series : [];
  const shape = series
    .map((entry) => {
      const item = (entry ?? {}) as Record<string, unknown>;
      const points = Array.isArray(item.points) ? item.points.length : 0;
      return `${String(item.id ?? "")}:${points}`;
    })
    .join(",");
  return `chart|${String(raw.title ?? "")}|${String(
    raw.chart_type ?? ""
  )}|${shape}`;
};

export const mapArtifactKey = (map: object): string => {
  const raw = map as Record<string, unknown>;
  const markers = Array.isArray(raw.markers) ? raw.markers.length : 0;
  return `map|${String(raw.title ?? "")}|${String(raw.map_type ?? "")}|${String(
    raw.image_url ?? ""
  )}|${markers}`;
};

export const dashboardArtifactKey = (dashboard: object): string => {
  const raw = dashboard as Record<string, unknown>;
  return `dashboard|${String(raw.project_id ?? "")}|${String(
    raw.dashboard_id ?? ""
  )}`;
};
